import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { Tables } from "@/types/supabase";
import {
  BarVisualizer,
  RoomAudioRenderer,
  RoomContext,
  useVoiceAssistant,
  VoiceAssistantControlBar,
} from "@livekit/components-react";
import "@livekit/components-styles";
import {
  ConnectionQuality,
  ConnectionState,
  DisconnectReason,
  Room,
  RoomEvent,
} from "livekit-client";
import {
  AlertCircle,
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  Play,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useV2WebCall } from "@/hooks/useV2WebCall";
import { useBackendFlags } from "@/hooks/useBackendFlags";
import { apiServerHeaders } from "@/lib/apiServerHeaders";
import { BackendPicker } from "@/components/shared/BackendPicker";
import { getAssistantLanguageSetting } from "@/services/assistantLanguageService";
import { StartLanguageSelect } from "@/components/shared/StartLanguageSelect";
import type { LanguageConfiguration } from "@/config/language-rules";
import { supabase } from "../../../supabase/supabase";

// Type definitions
interface TestAgentDialogProps {
  assistantConfig: any;
  assistantId?: string;
}

type ConnectionDetails = {
  serverUrl: string;
  roomName: string;
  participantIdentity: string;
  token: string;
  assistant_id: any;
  callId: string;
  backend: CallBackend;
  /** V2 only: media socket the browser streams the microphone to. */
  v2WsUrl?: string;
};

type CallBackend = "v1" | "v2";

/**
 * Map a teardown trigger to a call_logs.ended_reason value the backend
 * stores. Must be one of the values CALL_END_REASONS (src/utils/constants.ts)
 * recognizes - anything else renders as raw, malformed text in the call log
 * table instead of a clean label.
 */
function mapEndedReason(reason?: string): string {
  switch (reason) {
    case "agent-left":
      return "assistant-ended-web-session";
    case "user":
    case "unmount":
    default:
      return "customer-ended-web-session";
  }
}

/**
 * Tell the backend the call is over so the call_log stops sitting at "queued".
 * Safe to call more than once - the endpoint is idempotent enough for our use.
 *
 * For a web session this POST is the only thing that finalises the call_logs row
 * (no Plivo hangup webhook fires), so the reason is sent here or it is never
 * recorded.
 */
async function endCallOnServer(callId?: string, reason?: string) {
  if (!callId) return;

  try {
    const callServiceUrl = import.meta.env.VITE_CALL_SERVICE_URL || "";
    await fetch(`${callServiceUrl}/api/call/${callId}/end`, {
      method: "POST",
      headers: await apiServerHeaders(),
      body: JSON.stringify({ ended_reason: mapEndedReason(reason) }),
    });
  } catch (error) {
    // Never let cleanup failures surface to the user mid-teardown
    console.error("Failed to mark call as ended:", error);
  }
}

type CallEvent = {
  timestamp: string;
  type: string;
  description: string;
  status?: string;
};

/**
 * Call Event Log Component
 * Displays a list of call-related events with timestamps
 */
function CallEventLog({ events }: { events: CallEvent[] }) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // The panel is short and fills up quickly, so keep the newest event in view.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [events]);

  return (
    <div className="mt-4 border border-slate-700 rounded-md p-2 bg-slate-900 max-h-48 overflow-y-auto">
      <h3 className="text-sm font-medium mb-1 text-slate-100">Call Events</h3>
      <div className="space-y-1">
        {events.map((event, index) => (
          <div key={index} className="text-xs flex items-start">
            <span className="text-slate-400 min-w-24">
              {new Date(event.timestamp).toLocaleTimeString()}
            </span>
            <span className={`font-medium ${getEventColor(event.type)}`}>
              {event.type}:
            </span>
            <span className="ml-1 text-slate-100">{event.description}</span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

/**
 * Get color for event type
 * Tuned for the dark panel above - the 600/700 shades were unreadable.
 */
function getEventColor(eventType: string): string {
  switch (eventType) {
    case "Initiated":
      return "text-sky-400";
    case "Dialing":
      return "text-amber-300";
    case "Ringing":
      return "text-orange-300";
    case "Connected":
      return "text-emerald-400";
    case "Quality":
      return "text-cyan-300";
    case "Microphone":
      return "text-violet-300";
    case "Disconnected":
      return "text-rose-400";
    case "Error":
      return "text-red-400";
    default:
      return "text-slate-300";
  }
}

/**
 * Simple Voice Assistant Component
 * Shows audio visualization and agent state
 */
function SimpleVoiceAssistant() {
  const { state, audioTrack } = useVoiceAssistant();
  return (
    <div className="h-40">
      <BarVisualizer
        state={state}
        barCount={5}
        trackRef={audioTrack}
        style={{}}
      />
      <p className="text-center">{state}</p>
    </div>
  );
}

/**
 * LiveKit Voice Agent Component
 * Handles all LiveKit room connection and audio interactions
 */
function LiveKitVoiceAgent({
  connectionDetails,
}: {
  connectionDetails: ConnectionDetails;
}) {
  const [room] = useState(new Room());
  const [connected, setConnected] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [callStatus, setCallStatus] = useState<string>("initiated");
  const [callEvents, setCallEvents] = useState<CallEvent[]>([
    {
      timestamp: new Date().toISOString(),
      type: "Initiated",
      description: "Call setup started",
      status: "initiated",
    },
  ]);

  // Guards the teardown path - the button, the agent leaving and unmount can
  // all fire it, and only the first one should do the work.
  const endedRef = useRef(false);

  // Single teardown path: leave the room and tell the backend the call is over.
  const endCall = async (reason: string) => {
    if (endedRef.current) return;
    endedRef.current = true;

    console.log(`Ending test call (${reason})`);

    try {
      await room.disconnect();
    } catch (error) {
      console.error("Error disconnecting room:", error);
    }

    setConnected(false);
    setMicEnabled(false);
    await endCallOnServer(connectionDetails.callId, reason);
  };

  // Add an event to the call events log
  const addCallEvent = (type: string, description: string, status?: string) => {
    setCallEvents((prev) => [
      ...prev,
      {
        timestamp: new Date().toISOString(),
        type,
        description,
        status,
      },
    ]);

    // If status is provided, update the call status
    if (status) {
      setCallStatus(status);
    }
  };

  // Setup room event listeners
  useEffect(() => {
    // Setup all event listeners for room events to track call status
    room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
      switch (state) {
        case ConnectionState.Connecting:
          addCallEvent("Dialing", "Establishing connection to room", "dialing");
          break;
        case ConnectionState.Connected:
          addCallEvent(
            "Connected",
            "Successfully connected to room",
            "connected"
          );
          break;
        case ConnectionState.Disconnected:
          addCallEvent(
            "Disconnected",
            "Disconnected from room",
            "disconnected"
          );
          break;
      }
    });

    room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      let description = "Call ended";
      if (reason) {
        description = `Call ended: ${DisconnectReason[reason]}`;
      }
      addCallEvent("Disconnected", description, "disconnected");
    });

    room.on(
      RoomEvent.ConnectionQualityChanged,
      (quality: ConnectionQuality) => {
        let qualityText = "unknown";
        switch (quality) {
          case ConnectionQuality.Excellent:
            qualityText = "excellent";
            break;
          case ConnectionQuality.Good:
            qualityText = "good";
            break;
          case ConnectionQuality.Poor:
            qualityText = "poor";
            break;
        }
        addCallEvent("Quality", `Connection quality changed to ${qualityText}`);
      }
    );

    // Track participant events
    room.on(RoomEvent.ParticipantConnected, () => {
      addCallEvent("Ringing", "Remote participant connected", "ringing");
    });

    // The agent leaving - end-call tool, idle timeout or a crash - means the
    // test is over. Tear down instead of leaving the user in a dead room.
    room.on(RoomEvent.ParticipantDisconnected, () => {
      addCallEvent("Disconnected", "Agent left, ending call", "disconnected");
      void endCall("agent-left");
    });

    room.on(RoomEvent.MediaDevicesError, (e: Error) => {
      addCallEvent("Error", `Media device error: ${e.message}`, "error");
    });

    // Cleanup all event handlers
    return () => {
      room.removeAllListeners();
    };
  }, [room]);

  // Connect to room when connectionDetails are available
  useEffect(() => {
    const connectToRoom = async () => {
      try {
        // Validate server URL before attempting connection
        console.log("Full connection details:", connectionDetails);

        if (!connectionDetails || !connectionDetails.serverUrl) {
          throw new Error("LiveKit server URL is missing");
        }

        // Try-catch specifically for URL construction
        let serverUrl;
        try {
          // Test URL construction first (this will catch the same error that causes LiveKit to fail)
          new URL(connectionDetails.serverUrl);
          serverUrl = connectionDetails.serverUrl;
          console.log("URL validation passed for:", serverUrl);
        } catch (urlError) {
          // The URL is invalid, try to fix it
          console.warn(
            "Invalid URL format:",
            connectionDetails.serverUrl,
            urlError
          );

          // Try to construct a valid URL by ensuring protocol
          if (connectionDetails.serverUrl.includes("localhost")) {
            // Local development usually uses ws://
            serverUrl = `ws://${connectionDetails.serverUrl.replace(
              /^(https?:\/\/|wss?:\/\/)?/,
              ""
            )}`;
          } else {
            // Production usually uses wss://
            serverUrl = `wss://${connectionDetails.serverUrl.replace(
              /^(https?:\/\/|wss?:\/\/)?/,
              ""
            )}`;
          }

          console.log("Fixed server URL to:", serverUrl);

          // Validate the fixed URL
          try {
            new URL(serverUrl);
            console.log("Fixed URL is valid:", serverUrl);
          } catch (e) {
            console.error("Could not fix URL, using hardcoded fallback");
            // Last resort - hardcoded example URL (won't work but prevents crash)
            serverUrl = "wss://example.livekit.cloud";
          }
        }

        addCallEvent("Dialing", "Connecting to LiveKit room", "dialing");
        console.log("Connecting to LiveKit room:", connectionDetails.roomName);
        console.log(
          "Final server URL for connection:",
          serverUrl,
          connectionDetails
        );

        // Connect with robustly validated URL
        await room.connect(
          connectionDetails.serverUrl,
          connectionDetails.token,
          {
            autoSubscribe: true,
          }
        );

        console.log("Connected to room, enabling microphone...");
        await room.localParticipant.setMicrophoneEnabled(true);
        setConnected(true);
        setMicEnabled(true);
        addCallEvent(
          "Connected",
          "Successfully connected and enabled microphone",
          "connected"
        );
        console.log(
          "Successfully connected to LiveKit room and enabled microphone"
        );
      } catch (error) {
        console.error("Failed to connect to LiveKit room:", error);
        addCallEvent("Error", `Connection error: ${error.message}`, "error");
        toast({
          title: "Connection Error",
          description: `Failed to connect to the agent room: ${error.message}`,
          variant: "destructive",
        });
      }
    };

    if (connectionDetails) {
      console.log("Connection details available, attempting to connect...");
      console.log("Connection details:", connectionDetails);
      // Connect with a slight delay to allow UI to update
      const timer = setTimeout(() => {
        connectToRoom();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [connectionDetails, room]);

  // Disconnect when component unmounts (dialog closed, navigated away)
  useEffect(() => {
    return () => {
      void endCall("unmount");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle microphone
  const toggleMicrophone = async () => {
    if (connected) {
      const newState = !micEnabled;
      await room.localParticipant.setMicrophoneEnabled(newState);
      setMicEnabled(newState);
      addCallEvent(
        "Microphone",
        newState ? "Microphone enabled" : "Microphone disabled"
      );
    }
  };

  // Manually disconnect call
  const disconnectCall = async () => {
    addCallEvent("Disconnected", "User manually ended call", "disconnected");
    await endCall("user");
  };

  return (
    <RoomContext.Provider value={room}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between p-2 bg-slate-800 rounded text-white">
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full ${
                callStatus === "dialing"
                  ? "bg-amber-400 animate-pulse"
                  : callStatus === "ringing"
                  ? "bg-orange-400 animate-pulse"
                  : callStatus === "connected"
                  ? "bg-green-400"
                  : callStatus === "disconnected"
                  ? "bg-red-400"
                  : callStatus === "error"
                  ? "bg-red-600"
                  : "bg-blue-400"
              }`}
            ></div>
            <div>
              Room: {connectionDetails.roomName}{" "}
              <span className="text-xs opacity-75">({callStatus})</span>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMicrophone}
              className={micEnabled ? "text-green-400" : "text-red-400"}
            >
              {micEnabled ? (
                <Mic className="h-4 w-4" />
              ) : (
                <MicOff className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={disconnectCall}
              className="text-red-400"
            >
              <PhoneOff className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-evenly ">
          <SimpleVoiceAssistant />
          <VoiceAssistantControlBar />
        </div>

        <RoomAudioRenderer />
        <CallEventLog events={callEvents} />
      </div>
    </RoomContext.Provider>
  );
}

/**
 * V2 Voice Agent Component
 *
 * V2 has no LiveKit room - the browser streams mic audio straight to the V2
 * media socket, so this panel drives the useV2WebCall hook instead of a Room.
 */
function V2VoiceAgent({
  connectionDetails,
}: {
  connectionDetails: ConnectionDetails;
}) {
  const { start, disconnect, toggleMic, setOnClosed, connected, micEnabled } =
    useV2WebCall();
  const [callStatus, setCallStatus] = useState<string>("initiated");
  const [callEvents, setCallEvents] = useState<CallEvent[]>([
    {
      timestamp: new Date().toISOString(),
      type: "Initiated",
      description: "Call setup started",
      status: "initiated",
    },
  ]);
  useEffect(() => {
    // StrictMode runs this effect, tears it down, then runs it again. Starting
    // on a timer the cleanup can cancel means the throwaway pass never opens a
    // socket - and never ends the call log - which is what V1 does at :395.
    let started = false;

    const timer = setTimeout(() => {
      started = true;

      // Same teardown contract as V1: the call log must not sit at "queued".
      setOnClosed((reason) => {
        void endCallOnServer(connectionDetails.callId, reason);
      });

      start({
        wsUrl: connectionDetails.v2WsUrl!,
        onEvent: (event) => {
          setCallEvents((prev) => [...prev, event]);
          if (event.status) setCallStatus(event.status);
        },
      }).catch((err: any) => {
        const description = err?.message || "Failed to start V2 session";
        setCallStatus("error");
        setCallEvents((prev) => [
          ...prev,
          {
            timestamp: new Date().toISOString(),
            type: "Error",
            description,
            status: "error",
          },
        ]);
      });
    }, 300);

    return () => {
      clearTimeout(timer);
      if (started) disconnect("unmount");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between p-2 bg-slate-800 rounded text-white">
        <div className="flex items-center space-x-2">
          <div
            className={`w-2 h-2 rounded-full ${
              callStatus === "dialing"
                ? "bg-amber-400 animate-pulse"
                : callStatus === "connected"
                ? "bg-green-400"
                : callStatus === "disconnected"
                ? "bg-red-400"
                : callStatus === "error"
                ? "bg-red-600"
                : "bg-blue-400"
            }`}
          ></div>
          <div>
            Room: {connectionDetails.roomName}{" "}
            <span className="text-xs opacity-75">({callStatus})</span>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleMic}
            className={micEnabled ? "text-green-400" : "text-red-400"}
          >
            {micEnabled ? (
              <Mic className="h-4 w-4" />
            ) : (
              <MicOff className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => disconnect("user")}
            className="text-red-400"
          >
            <PhoneOff className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-center h-40">
        <p className="text-center text-muted-foreground">
          {connected ? "Listening - speak to the agent" : "Connecting..."}
        </p>
      </div>

      <CallEventLog events={callEvents} />
    </div>
  );
}

/**
 * Main Test Agent Dialog Component
 */
export default function ImprovedTestAgentDialog({
  assistantConfig,
  assistantId: propAssistantId,
}: TestAgentDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [agentStatus, setAgentStatus] = useState<
    "idle" | "connecting" | "connected" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [assistantData, setAssistantData] =
    useState<Tables<"assistants"> | null>(null);
  const [connectionDetails, setConnectionDetails] =
    useState<ConnectionDetails | null>(null);
  const { defaultBackend } = useBackendFlags();
  const [backend, setBackend] = useState<CallBackend>(defaultBackend);
  const [languageSetting, setLanguageSetting] =
    useState<LanguageConfiguration | null>(null);
  const [startLanguage, setStartLanguage] = useState("");

  // Reset back to the start screen when the dialog is closed, otherwise
  // reopening reconnects with a stale token and a room that no longer exists.
  const handleOpenChange = (open: boolean) => {
    if (open) {
      // The test runs the SAVED assistant, so read the saved language setting.
      setStartLanguage("");
      getAssistantLanguageSetting(propAssistantId).then(setLanguageSetting);
    }
    if (!open) {
      setConnectionDetails(null);
      setAgentStatus("idle");
      setErrorMessage(null);
      setIsLoading(false);
    }
  };

  // Fetch assistant data from Supabase if we have an ID
  useEffect(() => {
    const fetchAssistantFromUrl = async () => {
      // Use the assistant ID from props
      const assistantId = propAssistantId;

      if (assistantId) {
        try {
          const { data, error } = await supabase
            .from("assistants")
            .select("*")
            .eq("id", assistantId)
            .single();

          if (error) {
            console.error("Error fetching assistant:", error);
            return;
          }

          if (data) {
            setAssistantData(data);
          }
        } catch (error) {
          console.error("Error fetching assistant data:", error);
        }
      }
    };

    fetchAssistantFromUrl();
  }, [propAssistantId]);

  // Start test agent and get LiveKit connection details
  const startTestAgent = async () => {
    setIsLoading(true);
    setAgentStatus("connecting");
    setErrorMessage(null);

    try {
      // Get assistant ID from props, falling back to fetched data
      const assistantId = propAssistantId || assistantData?.id;

      // Get session for authentication
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.");
      }

      // Get Supabase details from environment
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // Call the unified API endpoint for web sessions
      // We use the same /api/call endpoint but with is_web_session=true
      // Use CALL_SERVICE_URL as the base URL for API calls
      const callServiceUrl = import.meta.env.VITE_CALL_SERVICE_URL || "";
      console.log("Using Call Service URL:", callServiceUrl);

      const response = await fetch(`${callServiceUrl}/api/call`, {
        method: "POST",
        // The user's login token: the API server checks membership of the assistant's org
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ...(assistantId && { assistant_id: assistantId }),
          is_web_session: true,
          backend,
          ...(backend === "v2" && startLanguage
            ? { start_language: startLanguage }
            : {}),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get token: ${errorText}`);
      }

      // Get connection details including token
      const data = await response.json();

      // Log the full response data for debugging
      console.log("API Response:", data);
      console.log("Server URL in response:", data.livekit_url);

      // Set connection details. V2 returns a media socket instead of a LiveKit
      // token; everything else in the response is the same.
      setConnectionDetails({
        serverUrl: data.livekit_url,
        roomName: data.room_name,
        participantIdentity: data.participant_identity,
        token: data.livekit_token,
        assistant_id: data.assistant_id,
        callId: data.call_id,
        backend: data.backend === "v2" ? "v2" : "v1",
        v2WsUrl: data.v2_ws_url,
      });

      // Update status to connected
      setAgentStatus("connected");

      // Show success toast
      toast({
        title: "Agent Started",
        description:
          "Your test agent is now running. You can interact with it in the dialog.",
      });
    } catch (error: any) {
      console.error("Error starting test agent:", error);
      setAgentStatus("error");
      setErrorMessage(error.message || "Unknown error occurred");

      toast({
        title: "Error Starting Agent",
        description:
          error.message ||
          "There was a problem starting the test agent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Play className="h-4 w-4" />
          Test Agent
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Test Voice Agent</DialogTitle>
          <DialogDescription>
            Test your voice agent configuration in real-time. Click "Start
            Agent" to begin the test.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {agentStatus === "idle" && (
            <div className="flex flex-col items-center justify-center p-8 border rounded-md">
              <p className="mb-4 text-center">
                Click the button below to start testing your agent with the
                current configuration.
              </p>

              <div className="mb-4 w-full max-w-xs">
                <p className="text-sm font-medium text-muted-foreground mb-2 text-center">
                  Backend
                </p>
                <BackendPicker value={backend} onChange={setBackend} disabled={isLoading} />
                {backend === "v2" && (
                  <p className="mt-2 text-xs text-muted-foreground text-center">
                    V2 streams your microphone straight to the V2 agent - no
                    LiveKit room is used.
                  </p>
                )}
              </div>

              {backend === "v2" && (
                <div className="mb-4 w-full max-w-xs">
                  <StartLanguageSelect
                    setting={languageSetting}
                    value={startLanguage}
                    onChange={setStartLanguage}
                    disabled={isLoading}
                  />
                  {languageSetting && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      The test runs the saved assistant, so language changes you
                      have not saved do not apply.
                    </p>
                  )}
                </div>
              )}

              <Button
                onClick={startTestAgent}
                disabled={isLoading}
                className="bg-primary hover:bg-primary/80 text-primary-foreground"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting Agent...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Start Agent
                  </>
                )}
              </Button>
            </div>
          )}

          {agentStatus === "connecting" && (
            <div className="flex flex-col items-center justify-center p-8 border rounded-md">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-center">
                Connecting to agent server and initializing voice agent...
              </p>
            </div>
          )}

          {agentStatus === "connected" && connectionDetails && (
            <div className="flex flex-col">
              <div className="bg-black text-green-400 p-2 rounded-t-md text-xs">
                {connectionDetails.backend === "v2"
                  ? `Connected to V2 session: ${connectionDetails.roomName}`
                  : `Connected to LiveKit room: ${connectionDetails.roomName}`}
              </div>
              <div className="p-4 border rounded-b-md">
                {connectionDetails.backend === "v2" ? (
                  <V2VoiceAgent connectionDetails={connectionDetails} />
                ) : (
                  <LiveKitVoiceAgent connectionDetails={connectionDetails} />
                )}
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                <p className="font-medium">Instructions:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Click "Allow" when prompted for microphone access</li>
                  <li>
                    Speak clearly into your microphone to interact with the
                    agent
                  </li>
                  <li>
                    The agent will respond based on your configuration settings
                  </li>
                  <li>
                    You can toggle the microphone on/off with the mic button
                  </li>
                  <li>Close this dialog when you're done testing</li>
                </ul>
              </div>
            </div>
          )}

          {agentStatus === "error" && (
            <div className="flex flex-col items-center justify-center p-8 border border-destructive rounded-md">
              <div className="flex items-center justify-center mb-4">
                <AlertCircle className="h-8 w-8 text-destructive mr-2" />
                <p className="text-destructive text-center font-semibold">
                  Error Starting Agent
                </p>
              </div>
              <p className="text-destructive mb-4 text-center">
                {errorMessage ||
                  "There was an error starting the test agent. Please check your configuration and try again."}
              </p>
              <div className="flex flex-col space-y-2 w-full max-w-md">
                <Button
                  onClick={startTestAgent}
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-destructive/10"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
